/**
 * Redux slice (src/store/slices/bookingSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  GetBookingsByStudentID,
  GetBookingsByTeacherID,
  GetExistingTeacherAvailability,
  GetCalendarDataByMonth,
  GetExistingTeacherAvailability__oF_Package,
  SearchBookingbyStudentUsername,
  UpdatetheBookingStatus,
  // New actions
  Add_booking,
  createTrialBooking,
  fetchAllbookings,
  fetchCompleteBookings,
  INVALID_COMPLETE_BOOKING_LIST_RESPONSE,
  isValidBookingListResponse,
  getBookingByCode,
  getTeacherAvailability,
  Updatebooking,
  markAttendance,
  cancelBooking,
  rescheduleBooking,
  completeBooking,
  Deletebooking,
  fetchBookingsExcludingStudent,
  updateJoinMethod,
  createInvite,
} from "../actions/bookingActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

const getBookingListScope = (params = {}, requestId = null) => ({
  requestId,
  role: String(params.role || (params.teacherId ? 'teacher' : params.studentId ? 'student' : 'unknown')),
  teacherId: params.teacherId ? String(params.teacherId) : null,
});

const sameBookingListScope = (left, right) => (
  left?.role === right?.role && left?.teacherId === right?.teacherId
);

const beginBookingListRequest = (state, action) => {
  const scope = getBookingListScope(action.meta?.arg, action.meta?.requestId || null);
  const confirmedScope = state.bookingListRequest?.confirmedScope || null;

  state.loading = true;
  state.error = null;
  state.bookingListRequest = {
    requestId: scope.requestId,
    status: 'loading',
    error: null,
    confirmedScope,
  };

  // A same-teacher refresh retains the confirmed schedule for the stale state,
  // but a different teacher must never see the previous teacher's bookings.
  if (scope.role === 'teacher' && confirmedScope && !sameBookingListScope(confirmedScope, scope)) {
    state.bookingList = [];
    state.pagination = null;
    state.summary = null;
    state.bookingListRequest.confirmedScope = null;
  }
};

const isCurrentBookingListRequest = (state, action) => {
  const requestId = action.meta?.requestId;
  return !requestId || state.bookingListRequest?.requestId === requestId;
};

const commitBookingListRequest = (state, action, { requireComplete = false } = {}) => {
  if (!isCurrentBookingListRequest(state, action)) return;

  const payload = action.payload;
  if (requireComplete && (!isValidBookingListResponse(payload) || payload?.pagination?.isComplete !== true)) {
    state.loading = false;
    state.error = INVALID_COMPLETE_BOOKING_LIST_RESPONSE;
    state.bookingListRequest.status = 'failed';
    state.bookingListRequest.error = INVALID_COMPLETE_BOOKING_LIST_RESPONSE;
    return;
  }

  state.loading = false;
  if (Array.isArray(payload?.bookings)) {
    state.bookingList = payload.bookings;
    state.pagination = payload.pagination || null;
    state.summary = payload.summary || null;
  } else {
    state.bookingList = [];
    state.pagination = null;
    state.summary = null;
  }
  state.error = null;
  state.bookingListRequest = {
    requestId: action.meta?.requestId || null,
    status: 'succeeded',
    error: null,
    confirmedScope: getBookingListScope(action.meta?.arg),
  };
};

const failBookingListRequest = (state, action) => {
  if (!isCurrentBookingListRequest(state, action)) return;

  state.loading = false;
  state.error = action.payload || 'Failed to fetch bookings';
  state.bookingListRequest.status = 'failed';
  state.bookingListRequest.error = state.error;
  if (action.meta?.arg?.suppressToast !== true) {
    toast.error(state.error);
  }
};

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  loading: false,
  bookingList: [],
  singleBooking: null,
  teacherAvailability: [],
  StudentID_Booking: [],
  Teacher_Bookings: [],
  Teacher_Availabile_Booking_Slots: [],
  error: null,
  // New state fields
  bookingByCode: null,
  pagination: null,
  summary: null,
  bookingListRequest: {
    requestId: null,
    status: 'idle',
    error: null,
    confirmedScope: null,
  },
  // Optimized calendar data
  calendarData: {
    bookings: [],
    availability: [],
    month: null,
    year: null,
    timezone: 'UTC',
    requestId: null,
    loading: false,
    error: null
  },
  // Invite management
  inviteLink: null,
  inviteLoading: false,
  inviteError: null,
  inviteSuccess: false,
  inviteData: null,
};

/**
 * RTK slice definition: bookingSlice.
 */
const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    // Add any synchronous reducers here if needed
    /**
     * Reducer: clearBookingErrors.
     */
    clearBookingErrors: (state) => {
      state.error = null;
    },
    /**
     * Reducer: clearBookingData.
     */
    clearBookingData: (state) => {
      state.singleBooking = null;
    },
    /**
     * Reducer: resetInviteState.
     */
    resetInviteState: (state) => {
      state.inviteLink = null;
      state.inviteLoading = false;
      state.inviteError = null;
      state.inviteSuccess = false;
      state.inviteData = null;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Existing reducers
      .addCase(GetBookingsByStudentID.fulfilled, (state, action) => {
        state.loading = false;
        state.StudentID_Booking = action.payload;
      })
      // extraReducers handler: GetBookingsByStudentID.rejected
      .addCase(GetBookingsByStudentID.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: GetBookingsByTeacherID.pending
      .addCase(GetBookingsByTeacherID.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: GetBookingsByTeacherID.fulfilled
      .addCase(GetBookingsByTeacherID.fulfilled, (state, action) => {
        state.Teacher_Bookings = action.payload;
        state.loading = false;
      })
      // extraReducers handler: GetBookingsByTeacherID.rejected
      .addCase(GetBookingsByTeacherID.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: GetExistingTeacherAvailability.pending
      .addCase(GetExistingTeacherAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: GetExistingTeacherAvailability.fulfilled
      .addCase(GetExistingTeacherAvailability.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.AvailableTimeSlots) {
          state.Teacher_Availabile_Booking_Slots =
            action.payload.AvailableTimeSlots;
        } else {
          state.error = "No available time slots";
          toast.error(t("store.booking.noBookingsAvailable"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: GetExistingTeacherAvailability.rejected
      .addCase(GetExistingTeacherAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // extraReducers handler: GetExistingTeacherAvailability__oF_Package.pending
      .addCase(GetExistingTeacherAvailability__oF_Package.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        GetExistingTeacherAvailability__oF_Package.fulfilled,
        (state, action) => {
          state.loading = false;
          if (
            action.payload &&
            action.payload.data &&
            action.payload.data.AvailableTimeSlots
          ) {
            state.Teacher_Availabile_Booking_Slots =
              action.payload.data.AvailableTimeSlots;
          } else {
            toast.error(t("store.booking.noBookingsAvailable"), {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
            });
          }
        }
      )
      // extraReducers handler: GetExistingTeacherAvailability__oF_Package.rejected
      .addCase(GetExistingTeacherAvailability__oF_Package.rejected, (state) => {
        state.loading = false;
      })
      // Optimized calendar data reducers
      .addCase(GetCalendarDataByMonth.pending, (state, action) => {
        state.calendarData.loading = true;
        state.calendarData.error = null;
        state.calendarData.requestId = action.meta?.requestId || null;
      })
      // extraReducers handler: GetCalendarDataByMonth.fulfilled
      .addCase(GetCalendarDataByMonth.fulfilled, (state, action) => {
        if ((action.meta?.requestId || null) !== (state.calendarData.requestId || null)) return;
        state.calendarData.loading = false;
        state.calendarData.bookings = action.payload.bookings || [];
        state.calendarData.availability = action.payload.availability || [];
        state.calendarData.month = action.payload.month;
        state.calendarData.year = action.payload.year;
        state.calendarData.timezone = action.payload.timezone || 'UTC';
        state.calendarData.error = null;
      })
      // extraReducers handler: GetCalendarDataByMonth.rejected
      .addCase(GetCalendarDataByMonth.rejected, (state, action) => {
        if ((action.meta?.requestId || null) !== (state.calendarData.requestId || null)) return;
        state.calendarData.loading = false;
        state.calendarData.error = action.payload || 'Failed to fetch calendar data';
      })
      // extraReducers handler: SearchBookingbyStudentUsername.pending
      .addCase(SearchBookingbyStudentUsername.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: SearchBookingbyStudentUsername.fulfilled
      .addCase(SearchBookingbyStudentUsername.fulfilled, (state, action) => {
        state.loading = false;
        if (
          action.payload &&
          action.payload.data &&
          action.payload.data.foundedBooking
        ) {
          state.bookingList = action.payload.data.foundedBooking;
        } else {
          state.bookingList = [];
          toast.warning(t("store.booking.noBookingsForStudent"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: SearchBookingbyStudentUsername.rejected
      .addCase(SearchBookingbyStudentUsername.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: UpdatetheBookingStatus.pending
      .addCase(UpdatetheBookingStatus.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: UpdatetheBookingStatus.fulfilled
      .addCase(UpdatetheBookingStatus.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(t("store.booking.statusUpdated"));
      })
      // extraReducers handler: UpdatetheBookingStatus.rejected
      .addCase(UpdatetheBookingStatus.rejected, (state) => {
        state.loading = false;
        toast.error(t("store.booking.statusUpdateFailed"));
      })

      // New reducers for the new booking actions
      .addCase(Add_booking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Add_booking.fulfilled
      .addCase(Add_booking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Add the new booking to the list
          state.bookingList = Array.isArray(state.bookingList)
            ? [...state.bookingList, action.payload.booking]
            : [action.payload.booking];
          state.singleBooking = action.payload.booking;
          toast.success(t("store.booking.createdSuccessfully"));
        }
      })
      // extraReducers handler: Add_booking.rejected
      .addCase(Add_booking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create booking";
        toast.error(state.error);
      })

      // extraReducers handler: createTrialBooking.pending
      .addCase(createTrialBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: createTrialBooking.fulfilled
      .addCase(createTrialBooking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Add the new trial booking to the list
          state.bookingList = Array.isArray(state.bookingList)
            ? [...state.bookingList, action.payload.booking]
            : [action.payload.booking];
          state.singleBooking = action.payload.booking;
          // Remove toast from here - let component handle it
          // toast.success("Trial booking created successfully");
        }
      })
      // extraReducers handler: createTrialBooking.rejected
      .addCase(createTrialBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create trial booking";
        // Remove toast from here - let component handle it
        // toast.error(state.error);
      })

      // extraReducers handler: fetchAllbookings.pending
      .addCase(fetchAllbookings.pending, (state, action) => {
        beginBookingListRequest(state, action);
      })
      // extraReducers handler: fetchAllbookings.fulfilled
      .addCase(fetchAllbookings.fulfilled, (state, action) => {
        commitBookingListRequest(state, action);
      })
      // extraReducers handler: fetchAllbookings.rejected
      .addCase(fetchAllbookings.rejected, (state, action) => {
        failBookingListRequest(state, action);
      })
      .addCase(fetchCompleteBookings.pending, (state, action) => {
        beginBookingListRequest(state, action);
      })
      .addCase(fetchCompleteBookings.fulfilled, (state, action) => {
        commitBookingListRequest(state, action, { requireComplete: true });
      })
      .addCase(fetchCompleteBookings.rejected, (state, action) => {
        failBookingListRequest(state, action);
      })

      // extraReducers handler: getBookingByCode.pending
      .addCase(getBookingByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getBookingByCode.fulfilled
      .addCase(getBookingByCode.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          state.bookingByCode = action.payload.booking;
        }
      })
      // extraReducers handler: getBookingByCode.rejected
      .addCase(getBookingByCode.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to find booking with provided code";
        toast.error(state.error);
      })

      // extraReducers handler: getTeacherAvailability.pending
      .addCase(getTeacherAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getTeacherAvailability.fulfilled
      .addCase(getTeacherAvailability.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.availability) {
          state.teacherAvailability = action.payload.availability;
        } else {
          state.teacherAvailability = [];
        }
      })
      // extraReducers handler: getTeacherAvailability.rejected
      .addCase(getTeacherAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch teacher availability";
        toast.error(state.error);
      })

      // extraReducers handler: Updatebooking.pending
      .addCase(Updatebooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Updatebooking.fulfilled
      .addCase(Updatebooking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Update the booking in the list
          const updatedBooking = action.payload.booking;
          state.bookingList = state.bookingList.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          );
          state.singleBooking = updatedBooking;
          toast.success(t("store.booking.updatedSuccessfully"));
        }
      })
      // extraReducers handler: Updatebooking.rejected
      .addCase(Updatebooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update booking";
        toast.error(state.error);
      })

      // extraReducers handler: markAttendance.pending
      .addCase(markAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: markAttendance.fulfilled
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Update the booking in the list
          const updatedBooking = action.payload.booking;
          state.bookingList = state.bookingList.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          );
          state.singleBooking = updatedBooking;
          toast.success(t("store.booking.attendanceMarked"));
        }
      })
      // extraReducers handler: markAttendance.rejected
      .addCase(markAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to mark attendance";
        toast.error(state.error);
      })

      // extraReducers handler: cancelBooking.pending
      .addCase(cancelBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: cancelBooking.fulfilled
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Update the booking in the list
          const updatedBooking = action.payload.booking;
          state.bookingList = state.bookingList.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          );
          state.singleBooking = updatedBooking;
          toast.success(t("store.booking.cancelledSuccessfully"));
        }
      })
      // extraReducers handler: cancelBooking.rejected
      .addCase(cancelBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to cancel booking";
        toast.error(state.error);
      })

      // extraReducers handler: rescheduleBooking.pending
      .addCase(rescheduleBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: rescheduleBooking.fulfilled
      .addCase(rescheduleBooking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Update the booking in the list
          const updatedBooking = action.payload.booking;
          state.bookingList = state.bookingList.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          );
          state.singleBooking = updatedBooking;
          toast.success(t("store.booking.rescheduledSuccessfully"));
        }
      })
      // extraReducers handler: rescheduleBooking.rejected
      .addCase(rescheduleBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reschedule booking";
        toast.error(state.error);
      })

      // extraReducers handler: completeBooking.pending
      .addCase(completeBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: completeBooking.fulfilled
      .addCase(completeBooking.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.booking) {
          // Update the booking in the list
          const updatedBooking = action.payload.booking;
          state.bookingList = state.bookingList.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          );
          state.singleBooking = updatedBooking;
          toast.success(t("store.booking.completedSuccessfully"));
        }
      })
      // extraReducers handler: completeBooking.rejected
      .addCase(completeBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to complete booking";
        toast.error(state.error);
      })

      // extraReducers handler: Deletebooking.pending
      .addCase(Deletebooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Deletebooking.fulfilled
      .addCase(Deletebooking.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted booking from the list
        state.bookingList = state.bookingList.filter(
          (booking) => booking._id !== action.payload.id
        );
        if (
          state.singleBooking &&
          state.singleBooking._id === action.payload.id
        ) {
          state.singleBooking = null;
        }
        toast.success(t("store.booking.deletedSuccessfully"));
      })
      // extraReducers handler: Deletebooking.rejected
      .addCase(Deletebooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete booking";
        toast.error(state.error);
      })
      // extraReducers handler: fetchBookingsExcludingStudent.pending
      .addCase(fetchBookingsExcludingStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchBookingsExcludingStudent.fulfilled
      .addCase(fetchBookingsExcludingStudent.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.bookings) {
          state.bookingList = action.payload.bookings;
          // If pagination data is provided, save it
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          }
        } else {
          state.bookingList = [];
        }
      })
      // extraReducers handler: fetchBookingsExcludingStudent.rejected
      .addCase(fetchBookingsExcludingStudent.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch bookings excluding student";
        toast.error(state.error);
      })
      // Update join method
      .addCase(updateJoinMethod.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updateJoinMethod.fulfilled
      .addCase(updateJoinMethod.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBooking = action.payload.booking;

        // Update in bookingList
        const index = state.bookingList.findIndex(b => b._id === updatedBooking._id);
        if (index !== -1) {
          state.bookingList[index] = { ...state.bookingList[index], ...updatedBooking };
        }
      })
      // extraReducers handler: updateJoinMethod.rejected
      .addCase(updateJoinMethod.rejected, (state) => {
        state.loading = false;
        state.error = "Error updating join method";
      })
      // Create invite handlers
      .addCase(createInvite.pending, (state) => {
        state.inviteLoading = true;
        state.inviteError = null;
        state.inviteSuccess = false;
      })
      // extraReducers handler: createInvite.fulfilled
      .addCase(createInvite.fulfilled, (state, action) => {
        state.inviteLoading = false;
        state.inviteSuccess = true;
        state.inviteData = action.payload;
        state.inviteLink = action.payload.inviteLink;
        state.inviteError = null;
      })
      // extraReducers handler: createInvite.rejected
      .addCase(createInvite.rejected, (state, action) => {
        state.inviteLoading = false;
        state.inviteError = action.payload || 'Failed to create invite';
        state.inviteSuccess = false;
      });
  },
});

export const { clearBookingErrors, clearBookingData, resetInviteState } = bookingSlice.actions;
export default bookingSlice.reducer;
